import { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { currentUserPreferencesSelector } from 'store/slices/userDetailsSlice';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useUpdateUserPreferencesMutation } from 'store/apis/userDetailsApi';
import { useIsDemoUser } from 'components/utilities/hooks/useIsDemoUser';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { CONTENT_DO_NOT_SHOW_AGAIN_SUCCESS_MESSAGE } from 'constants/successMessages';
import { CONTENT_DO_NOT_SHOW_AGAIN_ERROR_MESSAGE } from 'constants/errorMessages';
import { useShowBannerStyles } from 'utilities/hooks/useShowBanner/styles';

export const useShowBanner = (message, preferenceName, extraOptions = {}) => {
  const isDemoUser = useIsDemoUser();
  const { showBanner, closeSnackbar, showSuccess, showError } = useSnackbar();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const preferences = useSelector(currentUserPreferencesSelector);
  const { classes } = useShowBannerStyles();
  const bannerShownRef = useRef(false);

  const handleDoNotShowAgainChange = useCallback(() => {
    updateUserPreferences({
      postParams: { [preferenceName]: false },
      partial: true,
    })
      .unwrap()
      .then(() => {
        showSuccess(CONTENT_DO_NOT_SHOW_AGAIN_SUCCESS_MESSAGE);
      })
      .catch(() => {
        showError(CONTENT_DO_NOT_SHOW_AGAIN_ERROR_MESSAGE);
      });
  }, [
    preferenceName,
    updateUserPreferences,
    showSuccess,
    showError,
    closeSnackbar,
  ]);

  useEffect(() => {
    if (
      !isDemoUser &&
      preferences[preferenceName] !== false &&
      !bannerShownRef.current
    ) {
      bannerShownRef.current = true;

      const messageWithCheckbox = (
        <Box display="flex" flexDirection="column">
          {message}
          {extraOptions.dismissible && (
            <FormControlLabel
              className={classes.selectableItem}
              control={
                <Checkbox
                  size="small"
                  onChange={handleDoNotShowAgainChange}
                  className={classes.checkbox}
                />
              }
              label="Don't show again"
            />
          )}
        </Box>
      );
      showBanner(messageWithCheckbox, {
        persist: true,
      });
    }

    return () => {
      bannerShownRef.current = false;
      closeSnackbar();
    };
  }, []);

  useEffect(() => {
    if (preferences[preferenceName] === false) {
      closeSnackbar();
    }
  }, [preferences, preferenceName, closeSnackbar]);
};
